import React from "react";
import { Box, Flex } from "@chakra-ui/react";
import { MidiNumbers } from "react-piano";
import PropTypes from "prop-types";

const SubmitPiano = ({
  noteRange,
  keyColors,
  keyBorderColors,
  activeNotes,
  correctNotes,
  isSubmitted,
  playNote,
  stopNote,
  ...otherProps
}) => {
  const { first, last } = noteRange;
  const midiNumbers = Array.from(
    { length: last - first + 1 },
    (_, i) => first + i
  );

  const getKeyProps = (midiNumber) => {
    const { isAccidental } = MidiNumbers.getAttributes(midiNumber);
    const isActive = activeNotes.includes(midiNumber);
    const isCorrect = correctNotes.includes(midiNumber);
    const isUserSelected = isActive && !isCorrect; // Check if the user selected the note but it's incorrect

    return {
      backgroundColor:
        isCorrect && isSubmitted
          ? "green.500" // Green for correct notes
          : isUserSelected
          ? "blue.500" // Blue for user-selected notes (but incorrect)
          : keyColors[midiNumber] || (isAccidental ? "black" : "white"),
      borderColor: keyBorderColors[midiNumber] || "gray.300",
      color: isAccidental ? "white" : "black",
      width: "100%",
      height: "100%",
      borderWidth: isAccidental ? "1px" : "1px",
      borderStyle: "solid",
      borderRadius: isAccidental ? "0 0 4px 4px" : "0 0 6px 6px",
      cursor: "pointer",
      transition: "all 0.1s",
      _hover: { opacity: 0.9 },
      _active: { transform: "scale(0.98)" },
    };
  };

  const getNaturalKeyWidth = () => {
    const numNaturalKeys = midiNumbers.filter(
      (number) => !MidiNumbers.getAttributes(number).isAccidental
    ).length;
    return 100 / numNaturalKeys;
  };

  const getKeyPosition = (midiNumber) => {
    const { isAccidental, pitchName } = MidiNumbers.getAttributes(midiNumber);
    const naturalKeyWidth = getNaturalKeyWidth();

    if (!isAccidental) {
      // Count the number of natural keys before this one
      const naturalKeyIndex =
        midiNumbers.filter(
          (num) =>
            num <= midiNumber && !MidiNumbers.getAttributes(num).isAccidental
        ).length - 1;
      return naturalKeyIndex * naturalKeyWidth;
    } else {
      const prevNaturalKeyIndex =
        midiNumbers.filter(
          (num) =>
            num < midiNumber && !MidiNumbers.getAttributes(num).isAccidental
        ).length - 1;
      const accidentalKeyWidth = naturalKeyWidth * 0.65;
      return (
        prevNaturalKeyIndex * naturalKeyWidth +
        naturalKeyWidth -
        accidentalKeyWidth / 2
      );
    }
  };

  return (
    <Flex position="relative" {...otherProps}>
      {/* Render natural keys */}
      {midiNumbers.map((midiNumber) => {
        const { isAccidental } = MidiNumbers.getAttributes(midiNumber);
        if (isAccidental) return null;
        const left = `${getKeyPosition(midiNumber)}%`;
        const width = `${getNaturalKeyWidth()}%`;

        return (
          <Box
            key={midiNumber}
            position="absolute"
            left={left}
            width={width}
            height="100%"
            zIndex={0}
          >
            <Box
              {...getKeyProps(midiNumber)}
              onMouseDown={() => playNote(midiNumber)}
              onMouseUp={() => stopNote(midiNumber)}
              onMouseLeave={() => stopNote(midiNumber)}
            />
          </Box>
        );
      })}

      {/* Render accidental keys */}
      {midiNumbers.map((midiNumber) => {
        const { isAccidental } = MidiNumbers.getAttributes(midiNumber);
        if (!isAccidental) return null;
        const left = `${getKeyPosition(midiNumber)}%`;
        const width = `${getNaturalKeyWidth() * 0.65}%`;

        return (
          <Box
            key={midiNumber}
            position="absolute"
            left={left}
            width={width}
            height="60%"
            zIndex={1}
          >
            <Box
              {...getKeyProps(midiNumber)}
              onMouseDown={() => playNote(midiNumber)}
              onMouseUp={() => stopNote(midiNumber)}
              onMouseLeave={() => stopNote(midiNumber)}
            />
          </Box>
        );
      })}
    </Flex>
  );
};

SubmitPiano.propTypes = {
  noteRange: PropTypes.shape({
    first: PropTypes.number.isRequired,
    last: PropTypes.number.isRequired,
  }).isRequired,
  keyColors: PropTypes.objectOf(PropTypes.string),
  keyBorderColors: PropTypes.objectOf(PropTypes.string),
  activeNotes: PropTypes.arrayOf(PropTypes.number),
  correctNotes: PropTypes.arrayOf(PropTypes.number), // New prop for correct notes
  isSubmitted: PropTypes.bool,
  playNote: PropTypes.func.isRequired,
  stopNote: PropTypes.func.isRequired,
};

SubmitPiano.defaultProps = {
  keyColors: {},
  keyBorderColors: {},
  activeNotes: [],
  correctNotes: [],
  isSubmitted: false,
};

export default SubmitPiano;
